import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import $ from 'jquery';
import { env, EnvironmentValue } from 'env';

import appInsights from 'app-insights';

import { cashFlowApi } from 'store';
import { BaseApplicationKeyInformationType } from 'handlers/application';

import { CircularProgress } from 'components/Base';

import { ApplicationInformation } from 'api/rtkQueryApi';

import styles from './CashFlowWidget.module.scss';

interface InquiryData {
  inquiryId: string;
  inquiryType: string;
}

interface CashFlowData extends InquiryData {
  accountTokenId: string;
}

interface CashFlowWidgetProps {
  application: ApplicationInformation['application'];
  setData(data: CashFlowData): void;
}

interface RibbitEventData {
  name: string;
  sessionToken: string;
  CONNECTComplete: boolean;
  CONNECTType?: string;
  inquiryId?: string;
  inquiryType: string;
  accountToken?: string;
  customerId?: string;
  account: {
    bankId?: number;
    bankName?: string;
    accountType?: string;
    accountNickname?: string;
    last4Digits?: string;
  };
  accountNickname: string;
  last4Digits?: string;
}

async function initializeCashFlowWidget(
  {
    tag,
    application,
  }: {
    tag: HTMLDivElement;
    application: ApplicationInformation['application'];
  },
  {
    onReady,
    onEnrollmentSuccess,
    onRiskInquirySuccess,
  }: {
    onReady: () => void;
    onEnrollmentSuccess: (token: string) => void;
    onRiskInquirySuccess: (id: string, data: { inquiryId?: string; inquiryType: string }) => void;
  },
) {
  appInsights.trackEvent({
    name: 'CashFlowWidgetInitialization',
  });

  await import('jquery').then((module) => {
    window.$ = module.default;
    window.JQuery = module.default;
  });

  const widgetResponse = await cashFlowApi.getWidgetSession({
    firstName: application.variables[BaseApplicationKeyInformationType.FirstName],
    lastName: application.variables[BaseApplicationKeyInformationType.LastName],
    emailAddress: application.variables[BaseApplicationKeyInformationType.EmailAddress],
    phoneNumber: application.variables[BaseApplicationKeyInformationType.Phone],
    fullAmount: String(application.variables[BaseApplicationKeyInformationType.PrequalOfferLoanAmount]),
    amount: String(application.variables[BaseApplicationKeyInformationType.PrequalOfferEstimatedPayment]),
    streetAddress: String(application.variables.borrower_street_address),
    state: String(application.variables.borrower_state_or_province),
    city: String(application.variables.borrower_city),
    zip: String(application.variables.borrower_zip_or_postal_code),
  });

  const { RIBBITConnect } = window;

  if (!RIBBITConnect) {
    return;
  }

  const CONNECT = new RIBBITConnect({
    target: $(tag).attr('id'),
    token: widgetResponse.sessionToken,
    environment: env.REACT_APP_ENVIRONMENT === EnvironmentValue.Prod ? 'Production' : 'Test',
    inline: true,
    fullscreen: true,
    settings: {
      mobileWidth: 600,
    },
  });

  CONNECT.onLaunch(() => {
    onReady();

    CONNECT.open();
    appInsights.trackEvent({ name: 'CashFlowWidgetInitialized' });

    // TODO if its not useful - remove it
    CONNECT.onMessage((name: string, eventData: RibbitEventData) => {
      console.log('onMessage', name, eventData);
    });
  });

  CONNECT.onLinkOpen((url: string) => {
    window.open(url);
  });

  CONNECT.onBankLoginSelected((eventData: RibbitEventData) => {
    console.log('onBankLoginSelected', eventData);
    appInsights.trackEvent({
      name: 'CashFlowWidgetOnBankLoginSelected',
      properties: { bankName: eventData?.account?.bankName },
    });
  });

  CONNECT.onManualEnrollmentSelected((eventData: RibbitEventData) => {
    console.log('onManualEnrollmentSelected', eventData);
    appInsights.trackEvent({
      name: 'CashFlowWidgetOnManualEnrollmentSelected',
      properties: { bankName: eventData?.account?.bankName },
    });
  });

  CONNECT.onNoAccountsFound((eventData: RibbitEventData) => {
    console.log('onNoAccountsFound', eventData);
    appInsights.trackEvent({ name: 'CashFlowWidgetOnNoAccountsFound' });
  });

  CONNECT.onBankNotFound((eventData: RibbitEventData) => {
    console.log('onBankNotFound', eventData);
    appInsights.trackEvent({ name: 'CashFlowWidgetOnBankNotFound', properties: { phrase: '' } });
  });

  CONNECT.onBankLogin((eventData: RibbitEventData) => {
    console.log('onBankLogin', eventData);
    appInsights.trackEvent({ name: 'CashFlowWidgetOnBankLogin' });
  });

  CONNECT.onComplete((eventData: RibbitEventData) => {
    console.log('onComplete', eventData);
    if (eventData.accountToken) {
      onEnrollmentSuccess(eventData.accountToken);
      appInsights.trackEvent({
        name: 'CashFlowWidgetEnrollmentSuccess',
        properties: { token: eventData.accountToken },
      });
    }
    if (eventData.inquiryId) {
      onRiskInquirySuccess(eventData.inquiryId, eventData);
      appInsights.trackEvent({
        name: 'CashFlowWidgetRiskInquirySuccess',
        properties: { id: eventData.inquiryId, data: eventData },
      });
    }
  });

  CONNECT.onExit(() => {
    CONNECT.close();
    appInsights.trackEvent({ name: 'CashFlowWidgetOnExit' });
  });
}

const CashFlowWidget = ({ setData, application }: CashFlowWidgetProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [accountTokenId, setAccountTokenId] = useState<string | null>(null);
  const [inquiryData, setInquiryData] = useState<Partial<InquiryData>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const { current } = ref;

    setLoading(true);

    if (!current) {
      return;
    }

    initializeCashFlowWidget(
      {
        tag: current,
        application,
      },
      {
        onReady() {
          setLoading(false);
        },
        onEnrollmentSuccess(token) {
          setAccountTokenId(token);
        },
        onRiskInquirySuccess(id, data) {
          setInquiryData({ inquiryId: id, inquiryType: data.inquiryType });
        },
      },
    );
  }, []);

  useEffect(() => {
    const { inquiryId, inquiryType } = inquiryData;

    if (accountTokenId && inquiryType && inquiryId) {
      setData({ accountTokenId, inquiryId, inquiryType });
    }
  }, [accountTokenId, inquiryData]);

  const cashFlowWidgetStyle = classNames(styles.widget, {
    [styles.loading]: loading,
  });

  return (
    <div className={styles.container}>
      {loading && <CircularProgress centered />}
      <div id="vConnect" ref={ref} className={cashFlowWidgetStyle} />
    </div>
  );
};

export default CashFlowWidget;
